import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Contact } from "../components/Contact";
import { SmoothScroll, scrollToId } from "../components/SmoothScroll";
import { Reveal } from "../components/Reveal";
import { Seo, SITE_URL } from "../components/Seo";
import { useLang } from "../i18n/LangContext";
import { servicePages, servicePageLabels, SERVICE_SLUGS } from "../i18n/servicePages";

const ease = [0.7, 0, 0.2, 1];
const GROUP_IMG = { marketing: "/img/hero.jpg", web: "/img/interior.jpg", brand: "/img/brand2.jpg" };

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.2 + i * 0.14, ease }}>
      {children}
    </motion.span>
  </span>
);

const Eyebrow = ({ children, testid }) => <p className="eyebrow mb-8" data-testid={testid}>{children}</p>;

const ServiceHero = ({ s, group, labels }) => (
  <section className="relative pt-32 md:pt-40 pb-20 px-6 md:px-12 overflow-hidden" data-testid="service-hero">
    <div className="absolute inset-0 spotlight pointer-events-none" />
    <div className="relative grid lg:grid-cols-12 gap-14 items-end">
      <div className="lg:col-span-8">
        <Link to="/#services" data-testid="service-back-link" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-stone hover:text-gold transition-colors duration-300 mb-10">
          <ArrowLeft size={14} /> {labels.back}
        </Link>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="eyebrow mb-8" data-testid="service-overline">
          {group.label} — {s.name}
        </motion.p>
        <h1 className="font-display text-[2.7rem] leading-[0.98] sm:text-7xl lg:text-[6rem] tracking-[-0.02em]" data-testid="service-title">
          <MaskLine i={0}>{s.h1[0]}</MaskLine>
          <MaskLine i={1}><em className="italic text-gold font-normal">{s.h1[1]}</em></MaskLine>
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.8, ease }} className="mt-10 max-w-xl text-stone text-base md:text-lg leading-relaxed" data-testid="service-intro">
          {s.intro}
        </motion.p>
      </div>
      <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.5, delay: 0.4, ease }} className="lg:col-span-3 lg:col-start-10 aspect-[4/5] overflow-hidden max-w-xs w-full justify-self-end">
        <img src={GROUP_IMG[s.group]} alt={`${s.name} — ALLURE HOUSE`} className="w-full h-full object-cover grayscale contrast-[1.1]" />
      </motion.div>
    </div>
  </section>
);

const WhatWeDo = ({ s, labels }) => (
  <section className="px-6 md:px-12 py-24 md:py-32 border-t border-line" data-testid="service-what">
    <Reveal><Eyebrow>{labels.what}</Eyebrow></Reveal>
    <div className="grid md:grid-cols-2 gap-x-16 gap-y-14 mt-6">
      {s.what.map((w, i) => (
        <Reveal key={w.t} delay={i * 0.08} className="border-t border-line pt-6">
          <span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>
          <h2 className="mt-4 font-display text-3xl md:text-4xl">{w.t}</h2>
          <p className="mt-4 text-stone leading-relaxed max-w-md">{w.d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Deliverables = ({ s, labels }) => (
  <section className="px-6 md:px-12 py-24 md:py-32 bg-ink-2 border-t border-line" data-testid="service-deliverables">
    <div className="grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-4">
        <Eyebrow>{labels.deliverables}</Eyebrow>
        <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">{s.name}</h2>
      </Reveal>
      <ul className="lg:col-span-7 lg:col-start-6 grid sm:grid-cols-2 gap-x-10">
        {s.deliverables.map((d, i) => (
          <motion.li key={d} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.9, delay: i * 0.05, ease }} className="flex items-start gap-4 py-5 border-b border-line text-lg">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--gold)] shrink-0" />{d}
          </motion.li>
        ))}
      </ul>
    </div>
  </section>
);

const Process = ({ s, labels }) => (
  <section className="px-6 md:px-12 py-24 md:py-32 border-t border-line" data-testid="service-process">
    <Reveal><Eyebrow>{labels.process}</Eyebrow></Reveal>
    <ol className="grid md:grid-cols-4 gap-10 mt-6">
      {s.process.map((p, i) => (
        <motion.li key={p.t} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 1, delay: i * 0.1, ease }} className="border-t border-line pt-6">
          <span className="font-display italic text-4xl text-gold">{["I", "II", "III", "IV"][i]}</span>
          <h2 className="mt-4 font-display text-2xl md:text-3xl">{p.t}</h2>
          <p className="mt-3 text-stone text-sm leading-relaxed">{p.d}</p>
        </motion.li>
      ))}
    </ol>
  </section>
);

const Faq = ({ s, labels }) => (
  <section className="px-6 md:px-12 py-24 md:py-32 bg-ink-2 border-t border-line" data-testid="service-faq">
    <div className="grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-4"><Eyebrow>{labels.faq}</Eyebrow></Reveal>
      <div className="lg:col-span-7 lg:col-start-6">
        {s.faq.map((f, i) => (
          <Reveal key={f.q} delay={i * 0.06}>
            <details className="group border-b border-line py-6" data-testid={`service-faq-${i}`}>
              <summary className="flex items-center justify-between gap-6 cursor-pointer list-none font-display text-2xl md:text-3xl">
                {f.q}
                <ArrowUpRight className="shrink-0 text-stone group-open:rotate-90 group-open:text-gold transition-[transform,color] duration-500" size={22} />
              </summary>
              <p className="mt-4 text-stone leading-relaxed max-w-2xl">{f.a}</p>
            </details>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const OtherServices = ({ slug, pages, labels }) => (
  <section className="px-6 md:px-12 py-24 md:py-32 border-t border-line" data-testid="service-others">
    <Reveal><Eyebrow>{labels.other}</Eyebrow></Reveal>
    <ul className="mt-6 flex flex-wrap gap-3">
      {SERVICE_SLUGS.filter((x) => x !== slug).map((x) => (
        <li key={x}>
          <Link to={`/services/${x}`} data-testid={`service-other-${x}`} className="inline-block rounded-full border border-line px-5 py-2.5 text-sm hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors duration-300">
            {pages[x].name}
          </Link>
        </li>
      ))}
    </ul>
  </section>
);

const Cta = ({ labels }) => (
  <section className="px-6 md:px-12 py-24 md:py-32 border-t border-line" data-testid="service-cta">
    <Reveal>
      <Eyebrow>{labels.ctaOver}</Eyebrow>
      <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
        {labels.ctaTitle} <em className="text-gold italic font-normal">{labels.ctaTitle2}</em>
      </h2>
      <button data-testid="service-cta-btn" onClick={() => scrollToId("contact")} className="mt-12 group inline-flex items-center gap-3 rounded-full bg-[var(--gold)] text-[var(--ink)] pl-7 pr-2 py-2 font-semibold hover:bg-[var(--gold-2)] transition-colors duration-300">
        {labels.ctaBtn}
        <span className="w-10 h-10 rounded-full bg-[var(--ink)] text-[var(--gold)] flex items-center justify-center transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight size={18} /></span>
      </button>
    </Reveal>
  </section>
);

const buildJsonLd = (s, lang, slug) => ({
  "@context": "https://schema.org",
  "@graph": [
    { "@type": "Service", name: s.name, description: s.description, serviceType: s.name, inLanguage: lang,
      ...(SITE_URL ? { url: `${SITE_URL}/services/${slug}` } : {}),
      provider: { "@type": "Organization", name: "ALLURE HOUSE", ...(SITE_URL ? { url: `${SITE_URL}/` } : {}) },
      areaServed: lang === "fr" ? ["Genève", "Paris", "Londres"] : ["Geneva", "Paris", "London"] },
    { "@type": "FAQPage", mainEntity: s.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  ],
});

export default function ServicePage() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const pages = servicePages[lang];
  const labels = servicePageLabels[lang];
  const s = pages[slug];

  useEffect(() => { window.scrollTo(0, 0); window.__lenis?.scrollTo(0, { immediate: true }); }, [slug]);

  if (!s) {
    return (
      <div className="grain min-h-screen" data-testid="service-not-found">
        <Header />
        <main className="pt-48 px-6 md:px-12">
          <h1 className="font-display text-5xl">{labels.notFound}</h1>
          <Link to="/" className="mt-8 inline-block text-gold link-underline" data-testid="service-notfound-home-link">ALLURE HOUSE →</Link>
        </main>
      </div>
    );
  }

  const group = t.services_data.find((g) => g.key === s.group);
  return (
    <div className="grain" data-testid={`service-page-${slug}`}>
      <Seo title={s.title} description={s.description} jsonLd={buildJsonLd(s, lang, slug)} />
      <SmoothScroll />
      <Header />
      <main>
        <ServiceHero s={s} group={group} labels={labels} />
        <WhatWeDo s={s} labels={labels} />
        <Deliverables s={s} labels={labels} />
        <Process s={s} labels={labels} />
        <Faq s={s} labels={labels} />
        <Cta labels={labels} />
        <OtherServices slug={slug} pages={pages} labels={labels} />
        <Contact defaultServiceSlug={slug} />
      </main>
      <Footer />
    </div>
  );
}
